import {
  NavigateDown,
  NavigateUp,
  Redo,
} from '@randstad-lean-mobile-factory/react-assets/dist/icons';
import {
  ServerErrorRed,
  VidePasteque,
} from '@randstad-lean-mobile-factory/react-assets/dist/illustrations';
import {
  InfiniteData,
  InfiniteQueryObserverResult,
  QueryKey,
  UseInfiniteQueryResult,
  useMutation,
  UseMutationResult,
  UseQueryResult,
} from '@tanstack/react-query';
import * as reactQuery from '@tanstack/react-query';
import {
  Column,
  ColumnDef,
  ColumnFiltersState,
  ColumnPinningState,
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  Header,
  Row,
  RowData,
  RowModel,
  RowSelectionState,
  SortingState,
  Table as TablePrimitive,
  TableMeta,
  TableOptions,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import { omit, uniqBy } from 'lodash-es';
import {
  Dispatch,
  FunctionComponent,
  MouseEventHandler,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useInView } from 'react-intersection-observer';
import { LinkProps } from 'react-router-dom';

import { Button } from '../Button/Button';
import { CardCell, CardRow } from '../Cards';
import { ColumnFilterPopup } from '../DataTable/ColumnFilterPopup';
import { isFilterHandled } from '../DataTable/ColumnFilterPopup/ColumnFilterPopup.types';
import { globalFilterFn } from '../DataTable/DataTable.types';
import { Checkbox } from '../Forms';
import { InnerRadioButton } from '../Forms/RadioGroup/InnerRadioButton';
import { Loader } from '../Loader';
import {
  StyledTable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../StyledTable';

import styles from './SmartDataTable.module.scss';
import { SmartDataTableProps } from './SmartDataTable.types';

const hashKey = Object.keys(reactQuery).includes('hashKey') // @ts-expect-error ---
  ? reactQuery[(() => 'hashKey')() + (() => '')()]
  : // @ts-expect-error ---
    reactQuery[(() => 'hashQueryKey')() + (() => '')()];

const useTableState = <TData,>(initialState?: TableOptions<TData>['initialState']) => {
  const [sorting, setSorting] = useState<SortingState>(initialState?.sorting ?? []);
  const [globalFilter, setGlobalFilter] = useState<string>(initialState?.globalFilter ?? '');
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    initialState?.columnFilters ?? []
  );
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    initialState?.columnVisibility ?? {}
  );
  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>(
    initialState?.columnPinning ?? {}
  );
  const [rowSelection, setRowSelection] = useState<RowSelectionState>(
    initialState?.rowSelection ?? {}
  );
  const [expanded, setExpanded] = useState<ExpandedState>(initialState?.expanded ?? {});

  const resetState = useCallback(
    (options?: {
      sorting?: boolean;
      globalFilter?: boolean;
      columnVisibility?: boolean;
      columnPinning?: boolean;
      rowSelection?: boolean;
      expanded?: boolean;
    }) => {
      if (options?.sorting !== false) setSorting(initialState?.sorting ?? []);
      if (options?.globalFilter !== false) setGlobalFilter(initialState?.globalFilter ?? '');
      if (options?.columnVisibility !== false)
        setColumnVisibility(initialState?.columnVisibility ?? {});
      if (options?.columnPinning !== false) setColumnPinning(initialState?.columnPinning ?? {});
      if (options?.rowSelection !== false) setRowSelection(initialState?.rowSelection ?? {});
      if (options?.expanded !== false) setExpanded(initialState?.expanded ?? {});
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      // eslint-disable-next-line react-hooks/exhaustive-deps
      hashKey([initialState?.sorting]),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      hashKey([initialState?.expanded]),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      hashKey([initialState?.globalFilter]),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      hashKey([initialState?.columnVisibility]),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      hashKey([initialState?.columnPinning]),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      hashKey([initialState?.rowSelection]),
    ]
  );

  return {
    sorting,
    setSorting,
    globalFilter,
    setGlobalFilter,
    columnFilters,
    setColumnFilters,
    columnVisibility,
    setColumnVisibility,
    columnPinning,
    setColumnPinning,
    rowSelection,
    setRowSelection,
    expanded,
    setExpanded,
    resetState,
  };
};

declare module '@tanstack/table-core' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    name?: string;
    align?: 'left' | 'center' | 'right';
    disableDefaultFilter?: boolean;
    isDate?: boolean;
    colSpan?: (row: Row<TData>) => number;
    columnSortingLabel?: string;
    customFilter?: FunctionComponent<{
      column: Column<TData, TValue>;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- We have to use `any` here
      filterValue: any;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- We have to use `any` here
      setFilterValue: Dispatch<SetStateAction<any | undefined>>;
    }>;
  }

  interface TableMeta<TData extends RowData> {
    isMutating?: boolean;
    queryKey?: QueryKey;
    isError?: boolean;
    isLoading?: boolean;
    isFetching?: boolean;
    isRefetchError?: boolean;
    hasNextPage?: boolean;
    isFetchingNextPage?: boolean;
    isFetchNextPageError?: boolean;
    status?: UseQueryResult['status'];
    error?: unknown;
    refetch?: () => void;
    fetchNextPage?: () => Promise<InfiniteQueryObserverResult<unknown, unknown>>;
    enableRowAnimation: boolean;
    enableRowSelectionOnClick: boolean;
    enableRowExpansionOnClick: boolean;
    selectFromLeafRows: boolean;
    autoFetchNextPage: 'always' | 'onSelectAll' | 'never';
    onRowClick?: (row: Row<TData>) => void;
    navigateTo?: (row: Row<TData>) => LinkProps | undefined;
    allRowsSelected?: boolean;
    setAllRowsSelected?: (allRowsSelected: boolean) => void;
  }
}

interface UseTableProps<TData>
  extends Omit<
    TableOptions<TData>,
    | 'meta'
    | 'data'
    | 'columns'
    | 'getCoreRowModel'
    | 'onPaginationChange'
    | 'getPaginationRowModel'
    | 'getSortedRowModel'
    | 'getFilteredRowModel'
    | 'onSortingChange'
    | 'onGlobalFilterChange'
    | 'onColumnFiltersChange'
    | 'onColumnVisibilityChange'
    | 'onRowSelectionChange'
    | 'onExpandedChange'
    | 'state'
  > {
  data?: TData[];
  columns: (ColumnDef<TData> | Falsy)[];
  queryKey?: QueryKey;
  getUniqueId?: (originalRow: TData) => string;
  enableExpanding?: boolean;
  enableRowAnimation?: boolean;
  enableRowSelectionOnClick?: boolean;
  enableRowExpansionOnClick?: boolean;
  selectFromLeafRows?: boolean;
  autoFetchNextPage?: 'always' | 'onSelectAll' | 'never';
  onSortingChange?: (sorting: SortingState) => void | SortingState;
  onGlobalFilterChange?: (globalFilter: string) => void | string;
  onColumnFiltersChange?: (columnFilters: ColumnFiltersState) => void | ColumnFiltersState;
  onColumnVisibilityChange?: (columnVisibility: VisibilityState) => void | VisibilityState;
  onRowSelectionChange?: (rowSelection: RowSelectionState) => void | RowSelectionState;
  onExpandedChange?: (expanded: ExpandedState) => void | ExpandedState;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mutations?: UseMutationResult<any, any, any, any>[];
  resetOptions?: {
    sorting?: boolean;
    globalFilter?: boolean;
    columnVisibility?: boolean;
    columnPinning?: boolean;
    rowSelection?: boolean;
    expanded?: boolean;
  };
  onRowClick?: (row: Row<TData>) => void;
  navigateTo?: (row: Row<TData>) => LinkProps | undefined;
  meta?: Omit<
    TableMeta<TData>,
    | 'isMutating'
    | 'queryKey'
    | 'enableRowAnimation'
    | 'enableRowSelectionOnClick'
    | 'enableRowExpansionOnClick'
    | 'autoFetchNextPage'
    | 'selectFromLeafRows'
    | 'onRowClick'
    | 'navigateTo'
    | 'allRowsSelected'
    | 'setAllRowsSelected'
  >;
}

export interface UseTableResult<TData> extends TablePrimitive<TData> {
  fetchAllPages: UseMutationResult<RowModel<TData>, Error, void, unknown>;
  getSelectedRowIds: UseMutationResult<string[], Error, void, unknown>;
  getSelectedRowCount: () => { count: number; complete: boolean };
  getFullRowCount: () => { count: number; complete: boolean };
}

export const useTable = <TData,>({
  data,
  columns,
  queryKey,
  getUniqueId,
  enableRowSelection = false,
  enableExpanding = false,
  enableRowAnimation = true,
  enableRowSelectionOnClick = true,
  enableRowExpansionOnClick = true,
  selectFromLeafRows = false,
  autoFetchNextPage = 'never',
  onSortingChange: _onSortingChange,
  onGlobalFilterChange: _onGlobalFilterChange,
  onColumnFiltersChange: _onColumnFiltersChange,
  onColumnVisibilityChange: _onColumnVisibilityChange,
  onColumnPinningChange: _onColumnPinningChange,
  onRowSelectionChange: _onRowSelectionChange,
  onExpandedChange: _onExpandedChange,
  initialState,
  defaultColumn,
  onRowClick,
  navigateTo,
  resetOptions,
  mutations,
  meta,
  ...props
}: UseTableProps<TData>): UseTableResult<TData> => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableKey = useMemo(() => queryKey, [queryKey ? hashKey(queryKey) : undefined]);

  const {
    sorting,
    setSorting,
    globalFilter,
    setGlobalFilter,
    columnFilters,
    setColumnFilters,
    columnVisibility,
    setColumnVisibility,
    columnPinning,
    setColumnPinning,
    rowSelection,
    setRowSelection,
    expanded,
    setExpanded,
    resetState,
  } = useTableState(initialState);

  useEffect(() => {
    resetState(resetOptions);
    setAllRowsSelected(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stableKey]);

  const [isLoading, setIsLoading] = useState(meta?.isLoading);
  useEffect(() => setIsLoading(meta?.isLoading), [meta?.isLoading]);
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(meta?.isFetchingNextPage);
  useEffect(() => setIsFetchingNextPage(meta?.isFetchingNextPage), [meta?.isFetchingNextPage]);

  const uniqueData = useMemo(
    () => (getUniqueId ? uniqBy(data, getUniqueId) : (data ?? [])),
    [data, getUniqueId]
  );

  const [allRowsSelected, setAllRowsSelected] = useState(false);

  const fetchAllPagesInternal = useMutation({
    mutationFn: async () => {
      if (!meta?.hasNextPage) return tableRef.current.getRowModel();
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const result = await meta?.fetchNextPage?.();
        if (result?.isError) throw result.error;
        if (!result?.hasNextPage) {
          await new Promise(res => setTimeout(res, 50));
          return tableRef.current.getRowModel();
        }
      }
    },
    onSettled: () => {
      setTimeout(() => fetchAllPages.reset(), 500);
    },
  });
  const fetchAllPagesInternalRef = useRef(fetchAllPagesInternal);
  fetchAllPagesInternalRef.current = fetchAllPagesInternal;
  const fetchAllPages = useMutation({
    mutationFn: async () => {
      if (['loading', 'pending'].includes(fetchAllPagesInternalRef.current.status)) {
        while (['loading', 'pending'].includes(fetchAllPagesInternalRef.current.status)) {
          await new Promise(res => setTimeout(res, 50));
        }
        if (fetchAllPagesInternalRef.current.status === 'error')
          throw fetchAllPagesInternalRef.current.error;
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return fetchAllPagesInternalRef.current.data!;
      }
      return fetchAllPagesInternal.mutateAsync();
    },
    onSettled: () => {
      setTimeout(() => fetchAllPages.reset(), 500);
    },
  });

  useEffect(() => {
    if (meta?.hasNextPage && autoFetchNextPage === 'always') {
      fetchAllPages.mutate.call(undefined);
    }
  }, [autoFetchNextPage, fetchAllPages.mutate, meta?.hasNextPage]);

  const getSelectedRowIds = useMutation({
    mutationFn: async () => {
      if (!allRowsSelected)
        return Object.keys(rowSelection).filter(rowId => {
          if (!selectFromLeafRows) return true;
          const row = table.getRow(rowId);
          return !(row.getCanSelectSubRows() && row.subRows.length);
        });
      const rows = await fetchAllPages.mutateAsync();
      return rows.flatRows
        .filter(row => row.getCanSelect())
        .filter(row => {
          if (!selectFromLeafRows) return true;
          return !(row.getCanSelectSubRows() && row.subRows.length);
        })
        .map(row => row.id);
    },
    onSettled: () => {
      setTimeout(() => getSelectedRowIds.reset(), 500);
    },
  });

  const allColumns = useMemo(() => {
    const allColumns: (ColumnDef<TData, unknown> | false)[] = [
      enableExpanding && {
        id: 'expand',
        cell: ({ row }) =>
          row.getCanExpand() && (
            <Button
              variant="tertiary"
              size="medium"
              data-full-height
              data-no-padding-right
              onClick={() => row.toggleExpanded()}
            >
              {row.getIsExpanded() ? <NavigateUp /> : <NavigateDown />}
            </Button>
          ),
        size: 52,
        minSize: 52,
        maxSize: 52,
      },
      enableRowSelection && {
        id: 'select',
        header: ({ table }) => {
          if (table.options.enableMultiRowSelection === false) return null;
          return (
            <div
              onClick={() => {
                const checked = !table.getIsAllRowsSelected() && !table.getIsSomeRowsSelected();
                if (
                  table.options.meta?.hasNextPage ||
                  table.options.meta?.isFetchingNextPage !== undefined
                ) {
                  if (checked && table.options.meta?.autoFetchNextPage === 'onSelectAll')
                    fetchAllPages.mutate.call(undefined);

                  table.options.meta?.setAllRowsSelected?.(checked);
                  if (!checked) table.toggleAllRowsSelected(false);
                } else {
                  table.toggleAllRowsSelected(checked);
                }
              }}
              data-full-height
              data-no-padding-right
            >
              <Checkbox
                checked={
                  table.options.meta?.hasNextPage ||
                  table.options.meta?.isFetchingNextPage !== undefined
                    ? table.options.meta.allRowsSelected
                    : table.getIsAllRowsSelected()
                }
                disabled={table.options.meta?.isMutating}
                halfChecked={
                  table.options.meta?.hasNextPage
                    ? table.getIsSomeRowsSelected() || table.getIsAllRowsSelected()
                    : table.getIsSomeRowsSelected()
                }
                onChange={() => {}}
                aria-label="Select all"
              />
            </div>
          );
        },
        cell: ({ row, table }) => {
          if (!row.getCanSelect()) return null;
          if (table.options.enableMultiRowSelection === false)
            return (
              <div data-full-height data-no-padding-right>
                <InnerRadioButton
                  checked={row.getIsSelected()}
                  onChange={checked => row.toggleSelected(checked)}
                />
              </div>
            );
          return (
            <div
              onClick={event => event.stopPropagation()}
              style={{ display: 'contents' }}
              data-full-height
              data-no-padding-right
            >
              <Checkbox
                checked={
                  table.options.meta?.selectFromLeafRows &&
                  row.getCanSelectSubRows() &&
                  row.subRows.length
                    ? row.getIsAllSubRowsSelected()
                    : row.getIsSelected()
                }
                halfChecked={row.getIsSomeSelected()}
                disabled={table.options.meta?.isMutating}
                onClick={event => event.stopPropagation()}
                onChange={() => {
                  if (
                    table.options.meta?.selectFromLeafRows &&
                    row.getCanSelectSubRows() &&
                    row.subRows.length &&
                    row.getIsAllSubRowsSelected() &&
                    !row.getIsSelected()
                  ) {
                    table.options.onRowSelectionChange?.(selectionState =>
                      omit(
                        selectionState,
                        row.subRows.map(subRow => subRow.id)
                      )
                    );
                  } else row.toggleSelected();
                }}
              />
            </div>
          );
        },
        enableColumnFilter: false,
        enableGlobalFilter: false,
        enableSorting: false,
        enableHiding: false,
        size: 44,
        minSize: 44,
        maxSize: 44,
      },
      ...columns.filter(Boolean),
    ];
    return allColumns.filter(Boolean);
  }, [columns, enableExpanding, enableRowSelection, fetchAllPages.mutate]);

  const onSortingChange = useCallback<typeof setSorting>(
    updater => {
      if (typeof updater === 'function') updater = updater(sorting);
      if (_onSortingChange) {
        const value = _onSortingChange(updater);
        if (value !== undefined) updater = value;
      }
      setSorting(updater);
    },
    [_onSortingChange, setSorting, sorting]
  );

  const onGlobalFilterChange = useCallback<typeof setGlobalFilter>(
    updater => {
      if (typeof updater === 'function') updater = updater(globalFilter);
      if (_onGlobalFilterChange) {
        const value = _onGlobalFilterChange(updater);
        if (value !== undefined) updater = value;
      }
      setGlobalFilter(updater);
    },
    [_onGlobalFilterChange, setGlobalFilter, globalFilter]
  );

  const onColumnFiltersChange = useCallback<typeof setColumnFilters>(
    updater => {
      if (typeof updater === 'function') updater = updater(columnFilters);
      if (_onColumnFiltersChange) {
        const value = _onColumnFiltersChange(updater);
        if (value !== undefined) updater = value;
      }
      setColumnFilters(updater);
    },
    [_onColumnFiltersChange, setColumnFilters, columnFilters]
  );

  const onColumnVisibilityChange = useCallback<typeof setColumnVisibility>(
    updater => {
      if (typeof updater === 'function') updater = updater(columnVisibility);
      if (_onColumnVisibilityChange) {
        const value = _onColumnVisibilityChange(updater);
        if (value !== undefined) updater = value;
      }
      setColumnVisibility(updater);
    },
    [_onColumnVisibilityChange, setColumnVisibility, columnVisibility]
  );

  const onColumnPinningChange = useCallback<typeof setColumnPinning>(
    updater => {
      if (typeof updater === 'function') updater = updater(columnPinning);
      if (_onColumnPinningChange) {
        const value = _onColumnPinningChange(updater);
        if (value !== undefined) updater = value;
      }
      setColumnPinning(updater);
    },
    [_onColumnPinningChange, setColumnPinning, columnPinning]
  );

  const onRowSelectionChange = useCallback<typeof setRowSelection>(
    updater => {
      if (typeof updater === 'function') updater = updater(rowSelection);
      if (Object.keys(updater).length < Object.keys(rowSelection).length) setAllRowsSelected(false);
      if (_onRowSelectionChange) {
        const value = _onRowSelectionChange(updater);
        if (value !== undefined) updater = value;
      }
      setRowSelection(updater);
    },
    [_onRowSelectionChange, setRowSelection, rowSelection]
  );

  const onExpandedChange = useCallback<typeof setExpanded>(
    updater => {
      if (typeof updater === 'function') updater = updater(expanded);
      if (_onExpandedChange) {
        const value = _onExpandedChange(updater);
        if (value !== undefined) updater = value;
      }
      setExpanded(updater);
    },
    [_onExpandedChange, setExpanded, expanded]
  );

  const isMutating: boolean = [getSelectedRowIds]
    .concat(mutations ?? [])
    .some(mutation => ['loading', 'pending'].includes(mutation.status));

  const tableProps = useMemo((): TableOptions<TData> => {
    return {
      data: uniqueData,
      columns: allColumns,
      getCoreRowModel: getCoreRowModel(),
      onSortingChange,
      getSortedRowModel: getSortedRowModel(),
      onGlobalFilterChange,
      onColumnFiltersChange,
      getFilteredRowModel: getFilteredRowModel(),
      onColumnVisibilityChange,
      onColumnPinningChange,
      onRowSelectionChange,
      enableRowSelection,
      enableExpanding,
      getExpandedRowModel: enableExpanding ? getExpandedRowModel() : undefined,
      onExpandedChange,
      globalFilterFn,
      state: {
        sorting,
        globalFilter,
        columnFilters,
        columnVisibility,
        columnPinning,
        rowSelection,
        expanded,
      },
      defaultColumn: {
        size: undefined,
        minSize: undefined,
        maxSize: undefined,
        ...defaultColumn,
      },
      meta: {
        ...meta,
        isMutating,
        queryKey: stableKey,
        isLoading,
        isFetchingNextPage,
        enableRowAnimation,
        enableRowSelectionOnClick,
        enableRowExpansionOnClick,
        selectFromLeafRows,
        autoFetchNextPage,
        onRowClick,
        navigateTo,
        allRowsSelected,
        setAllRowsSelected,
      },
    };
  }, [
    uniqueData,
    allColumns,
    onSortingChange,
    onGlobalFilterChange,
    onColumnFiltersChange,
    onColumnVisibilityChange,
    onColumnPinningChange,
    onRowSelectionChange,
    enableRowSelection,
    enableExpanding,
    onExpandedChange,
    sorting,
    globalFilter,
    columnFilters,
    columnVisibility,
    columnPinning,
    rowSelection,
    expanded,
    defaultColumn,
    meta,
    isMutating,
    stableKey,
    isLoading,
    isFetchingNextPage,
    enableRowAnimation,
    enableRowSelectionOnClick,
    enableRowExpansionOnClick,
    selectFromLeafRows,
    autoFetchNextPage,
    onRowClick,
    navigateTo,
    allRowsSelected,
  ]);

  const table: TablePrimitive<TData> = useReactTable({ ...tableProps, ...props });
  const tableRef = useRef(table);
  tableRef.current = table;

  const rows = table.getRowModel().rows;

  useEffect(() => {
    if (allRowsSelected) table.toggleAllRowsSelected(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allRowsSelected, rows]);

  const getSelectedRowCount = useCallback(
    () => ({
      count: Object.keys(rowSelection).filter(rowId => {
        if (!selectFromLeafRows) return true;
        const row = table.getRow(rowId);
        return !(row.getCanSelectSubRows() && row.subRows.length);
      }).length,
      complete: !allRowsSelected || !meta?.hasNextPage,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rowSelection, allRowsSelected, meta?.hasNextPage, selectFromLeafRows]
  );

  const getFullRowCount = useCallback(
    () => ({
      count: uniqueData.length,
      complete: !meta?.hasNextPage,
    }),
    [uniqueData.length, meta?.hasNextPage]
  );

  return { ...table, fetchAllPages, getSelectedRowIds, getSelectedRowCount, getFullRowCount };
};

interface UseQueryTableProps<TData>
  extends Omit<UseTableProps<TData>, 'data' | 'meta' | 'queryKey'> {
  query: UseQueryResult<TData[], unknown> & { key?: QueryKey };
}

export const useQueryTable = <TData,>({ query, ...props }: UseQueryTableProps<TData>) => {
  const meta = useMemo(
    () => ({
      isError: query.isError,
      isLoading: query.isLoading,
      isFetching: query.isFetching,
      isRefetchError: query.isRefetchError,
      status: query.status,
      error: query.error,
      refetch: query.refetch,
    }),
    [
      query.error,
      query.isError,
      query.isFetching,
      query.isLoading,
      query.isRefetchError,
      query.refetch,
      query.status,
    ]
  );

  return useTable({ data: query.data, meta, queryKey: query.key, ...props });
};

interface UseInfiniteQueryTableProps<TData>
  extends Omit<UseTableProps<TData>, 'data' | 'meta' | 'queryKey' | 'enablePagination'> {
  query:
    | (UseInfiniteQueryResult<InfiniteData<{ data: TData[] }>, unknown> & { key?: QueryKey })
    | (UseInfiniteQueryResult<{ data: TData[] }, unknown> & { key?: QueryKey });
}

/* eslint-disable @typescript-eslint/no-explicit-any */
export type TableType<T> =
  T extends UseQueryResult<(infer K)[], any>
    ? K
    : T extends UseInfiniteQueryResult<InfiniteData<{ data: (infer K)[] }>, any>
      ? K
      : T extends UseInfiniteQueryResult<{ data: (infer K)[] }, any>
        ? K
        : T;
/* eslint-enable @typescript-eslint/no-explicit-any */

type Falsy = false | 0 | '' | null | undefined;
export type TableColumns<T> = (ColumnDef<TableType<T>> | Falsy)[];

export const useInfiniteQueryTable = <TData,>({
  query,
  ...props
}: UseInfiniteQueryTableProps<TData>) => {
  // For compatibility with both tanstack query versions 4 and 5
  const queryData = query.data as InfiniteData<{ data: TData[] }> | undefined;
  const data = useMemo(() => queryData?.pages.flatMap(page => page.data), [queryData?.pages]);

  const meta = useMemo(
    () => ({
      isError: query.isError,
      isLoading: query.isLoading,
      isFetching: query.isFetching,
      isRefetchError: query.isRefetchError,
      hasNextPage: query.hasNextPage,
      isFetchingNextPage: query.isFetchingNextPage,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      isFetchNextPageError: query.isFetchNextPageError ?? (!data ? false : (query as any).isError),
      status: query.status,
      error: query.error,
      refetch: query.refetch,
      fetchNextPage: query.fetchNextPage,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      data,
      query.error,
      query.fetchNextPage,
      query.hasNextPage,
      query.isError,
      query.isFetchNextPageError,
      query.isFetching,
      query.isFetchingNextPage,
      query.isLoading,
      query.isRefetchError,
      query.refetch,
      query.status,
    ]
  );

  return useTable({ data, meta, queryKey: query.key, ...props });
};

const DataTableHead = <TData,>({ header }: { header: Header<TData, unknown> }) => {
  const size = header.column.columnDef.size;
  const minSize = header.column.columnDef.minSize;
  const maxSize = header.column.columnDef.maxSize;
  const pinned = header.column.getIsPinned();

  return (
    <TableHead
      style={{
        width: size && `${size}px`,
        minWidth: minSize && `${minSize}px`,
        maxWidth: maxSize && `${maxSize}px`,
      }}
      pinned={pinned}
      pinDistance={pinned === 'left' ? header.column.getStart() : header.column.getAfter()}
    >
      <div
        className={styles.filterHeader}
        data-no-padding-right={
          isFilterHandled(header.column) || header.column.getCanSort() ? true : undefined
        }
      >
        {header.isPlaceholder
          ? null
          : flexRender(header.column.columnDef.header, header.getContext())}
        {isFilterHandled(header.column) || header.column.getCanSort() ? (
          <>
            <div className={styles.spacer} />
            <ColumnFilterPopup column={header.column} className={styles.filterButton} />
          </>
        ) : null}
      </div>
    </TableHead>
  );
};

const DataTableHeader = <TData,>({
  table,
  headerColor,
}: {
  table: TablePrimitive<TData>;
  headerColor: SmartDataTableProps<TData>['headerColor'];
}) => (
  <>
    {table.getHeaderGroups().map(headerGroup => (
      <TableRow key={headerGroup.id} color={headerColor}>
        {headerGroup.headers.map(header => (
          <DataTableHead key={header.id} header={header} />
        ))}
      </TableRow>
    ))}
  </>
);

const DataTableRow = <TData,>({
  rowStyle = 'classic',
  table,
  headerColor,
  row,
  alternateBackgrounds,
}: Pick<SmartDataTableProps<TData>, 'rowStyle' | 'table' | 'headerColor'> & {
  row: Row<TData>;
  alternateBackgrounds: boolean;
}) => {
  const to = useMemo(
    () => table.options.meta?.navigateTo?.call(undefined, row),
    [table.options.meta?.navigateTo, row]
  );

  const onClick = useCallback<MouseEventHandler<HTMLAnchorElement>>(
    event => {
      if (
        table.options.enableExpanding &&
        table.options.meta?.enableRowExpansionOnClick &&
        row.getCanExpand()
      ) {
        row.toggleExpanded();
        event.preventDefault();
        return;
      } else if (
        table.options.enableRowSelection &&
        table.options.meta?.enableRowSelectionOnClick &&
        !table.options.meta?.isMutating &&
        row.getCanSelect()
      ) {
        row.toggleSelected();
        event.preventDefault();
        return;
      }

      table.options.meta?.onRowClick?.call(undefined, row);
    },
    [
      row,
      table.options.enableExpanding,
      table.options.enableRowSelection,
      table.options.meta?.enableRowExpansionOnClick,
      table.options.meta?.enableRowSelectionOnClick,
      table.options.meta?.isMutating,
      table.options.meta?.onRowClick,
    ]
  );

  const color = alternateBackgrounds
    ? table
        .getRowModel()
        .rows.filter(row => row.depth === 0)
        .indexOf(row.getParentRows()[0] ?? row) %
        2 ===
      0
      ? headerColor === 'beige'
        ? ('white' as const)
        : ('beige' as const)
      : headerColor === 'beige'
        ? ('beige' as const)
        : ('white' as const)
    : undefined;

  const isClickable =
    !!table.options.meta?.onRowClick ||
    (table.options?.enableRowSelection &&
      table.options.meta?.enableRowSelectionOnClick &&
      !table.options.meta?.isMutating &&
      row.getCanSelect()) ||
    (table.options?.enableExpanding &&
      table.options.meta?.enableRowExpansionOnClick &&
      row.getCanExpand());

  if (rowStyle === 'classic') {
    return (
      <>
        <TableRow
          key={row.id}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          color={color as any}
          onClick={onClick}
          isSubRow={row.depth > 0}
          isClickable={isClickable}
          isCollapsed={!row.getParentRows().every(row => row.getIsExpanded())}
          isSelected={row.getIsSelected()}
          data-state={row.getIsSelected() && 'selected'}
          {...to}
        >
          {row.getVisibleCells().map(cell => {
            const pinned = cell.column.getIsPinned();
            return (
              <TableCell
                key={cell.id}
                align={cell.column.columnDef.meta?.align}
                pinned={pinned}
                pinDistance={pinned === 'left' ? cell.column.getStart() : cell.column.getAfter()}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            );
          })}
        </TableRow>
        {table.options.enableExpanding &&
          table.options.meta?.enableRowAnimation &&
          table.getRowModel().flatRows.length < 200 &&
          row.subRows.map(subRow => (
            <DataTableRow
              key={subRow.id}
              rowStyle={rowStyle}
              table={table}
              row={subRow}
              alternateBackgrounds={alternateBackgrounds}
              headerColor={headerColor}
            />
          ))}
      </>
    );
  }
  return (
    <CardRow
      key={row.id}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      color={color as any}
      onClick={onClick}
      isClickable={isClickable}
      isSelected={row.getIsSelected()}
      data-state={row.getIsSelected() && 'selected'}
      {...to}
    >
      {row.getVisibleCells().map(cell => {
        const pinned = cell.column.getIsPinned();
        return (
          <CardCell
            key={cell.id}
            align={cell.column.columnDef.meta?.align}
            pinned={pinned}
            pinDistance={pinned === 'left' ? cell.column.getStart() : cell.column.getAfter()}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </CardCell>
        );
      })}
    </CardRow>
  );
};

export interface DataTableProps<TData> {
  table: TablePrimitive<TData>;
  className?: string;
}

export const SmartDataTable = <TData,>({
  table,
  rowStyle,
  color,
  size,
  stickyHeader,
  stickyHeaderDistance,
  className,
  containerClassName,
  headerColor,
  emptyScreen,
  noVisibleColumnsScreen,
  loadingScreen,
  errorScreen,
  fetchingNextPageScreen,
  fetchingNextPageErrorScreen,
}: SmartDataTableProps<TData>) => {
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);

  useEffect(() => {
    if (table.options.meta?.isFetchingNextPage) setIsFetchingNextPage(true);
    else {
      if (table.options.meta?.isFetchNextPageError) setIsFetchingNextPage(false);
      if (!table.options.meta?.hasNextPage) setIsFetchingNextPage(false);
    }
  }, [
    table.options.meta?.hasNextPage,
    table.options.meta?.isFetchNextPageError,
    table.options.meta?.isFetchingNextPage,
  ]);

  const { ref, inView } = useInView({
    onChange: inView => {
      if (
        inView &&
        table.options.meta?.hasNextPage &&
        !table.options.meta?.isFetchingNextPage &&
        !table.options.meta?.isFetchNextPageError
      )
        table.options.meta?.fetchNextPage?.();
    },
  });
  useEffect(() => {
    if (
      inView &&
      table.options.meta?.hasNextPage &&
      !table.options.meta?.isFetchingNextPage &&
      !table.options.meta?.isFetchNextPageError
    )
      table.options.meta?.fetchNextPage?.call(undefined);
  }, [
    inView,
    table.options.meta?.fetchNextPage,
    table.options.meta?.hasNextPage,
    table.options.meta?.isFetchingNextPage,
    table.options.meta?.isFetchNextPageError,
  ]);

  const rowModel = table.getRowModel();

  const visibleColumns = table
    ?.getVisibleLeafColumns()
    .filter(column => !!column.columnDef.meta?.name || typeof column.columnDef.header === 'string');

  return (
    <>
      <StyledTable
        color={color}
        size={size}
        stickyHeader={stickyHeader}
        stickyHeaderDistance={stickyHeaderDistance}
        className={className}
        containerClassName={containerClassName}
      >
        <TableHeader>
          <DataTableHeader table={table} headerColor={headerColor} />
        </TableHeader>
        <TableBody>
          {visibleColumns.length && rowModel.rows.length
            ? rowModel.rows
                .filter(row => {
                  if (table.options.meta?.enableRowAnimation && rowModel.flatRows.length < 200) {
                    return row.depth === 0;
                  }
                  return true;
                })
                .map(row => (
                  <DataTableRow
                    key={row.id}
                    rowStyle={rowStyle}
                    table={table}
                    row={row}
                    headerColor={headerColor}
                    alternateBackgrounds={color === 'mixed'}
                  />
                ))
            : null}
        </TableBody>
      </StyledTable>
      {!visibleColumns.length ? (
        <div className={styles.emptyRow}>
          {noVisibleColumnsScreen ?? (
            <>
              <VidePasteque /> aucune colonne sélectionnée
            </>
          )}
        </div>
      ) : rowModel.rows.length ? null : table.options.meta?.isLoading ? (
        <div className={styles.emptyRow}>
          {loadingScreen ?? (
            <>
              <Loader data-full-height size="small" /> chargement des données en cours
            </>
          )}
        </div>
      ) : table.options.meta?.isError &&
        !table.options.meta.isRefetchError &&
        !table.options.meta.isFetchNextPageError ? (
        <div className={styles.emptyRow}>
          {errorScreen ?? (
            <>
              <ServerErrorRed />
              désolé, une erreur est survenue
              {table.options.meta?.refetch && (
                <Button size="medium" onClick={() => table.options.meta?.refetch?.()}>
                  <Redo />
                  réessayer
                </Button>
              )}
            </>
          )}
        </div>
      ) : !table.options.meta?.hasNextPage ? (
        <div className={styles.emptyRow}>
          {emptyScreen ?? (
            <>
              <VidePasteque /> aucune donnée correspondante
            </>
          )}
        </div>
      ) : null}
      {!visibleColumns.length ? null : isFetchingNextPage ? (
        <div className={styles.emptyRow}>
          {fetchingNextPageScreen ?? loadingScreen ?? (
            <>
              <Loader data-full-height size="small" /> chargement des données en cours
            </>
          )}
        </div>
      ) : table.options.meta?.isFetchNextPageError ? (
        <div className={styles.emptyRow}>
          {fetchingNextPageErrorScreen ?? errorScreen ?? (
            <>
              <ServerErrorRed />
              désolé, une erreur est survenue
              {table.options.meta?.fetchNextPage && (
                <Button size="medium" onClick={() => table.options.meta?.fetchNextPage?.()}>
                  <Redo />
                  réessayer
                </Button>
              )}
            </>
          )}
        </div>
      ) : null}
      {visibleColumns.length && table.options.meta?.hasNextPage ? <div ref={ref} /> : null}
    </>
  );
};
